import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";
import { COURSE } from "../../data/course";
import NetworkVisual from "./NetworkVisual";

const EASE = [0.16, 1, 0.3, 1];

// Masked line reveal
const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.12em]">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const visualY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden pt-28 pb-16 lg:flex lg:items-center"
    >
      {/* Fine grid across the whole hero + soft glows */}
      <div className="nx-grid pointer-events-none absolute inset-0" />
      <div className="pointer-events-none absolute -left-40 top-20 h-[520px] w-[520px] glow-blue opacity-70" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-[480px] w-[480px] glow-red opacity-40" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_72%_42%,rgba(41,56,145,0.14),transparent_58%)]" />

      <motion.div
        style={{ y: yText, opacity }}
        className="relative z-10 mx-auto w-full max-w-[1400px] px-6 md:px-12 lg:pr-[44%]"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mb-8 inline-flex items-center gap-2 rounded-full border border-[#293891]/15 bg-[#293891]/[0.03] px-4 py-1.5 text-xs font-medium uppercase tracking-[0.22em] text-[#3A4152] backdrop-blur-sm"
        >
          <Sparkles size={13} className="text-[#e63329]" />
          ExcellenC · Professional Program
        </motion.div>

        <h1 className="font-display text-[12vw] font-semibold leading-[0.98] tracking-tight text-[#293891] sm:text-[9vw] lg:text-[5.6vw] xl:text-[4.9rem]">
          <Line delay={0.35}>AI-Powered</Line>
          <Line delay={0.5}>
            <span className="text-stroke">Data Analytics</span>
          </Line>
          <Line delay={0.65}>
            <span className="text-[#e63329]">Professional</span>
          </Line>
          <Line delay={0.78}>Program</Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.95 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-[#3A4152] md:text-lg"
        >
          {COURSE.overview}
        </motion.p>

        {/* Badges */}
        <motion.div
          initial="hidden"
          animate="show"
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 1.15 } } }}
          className="mt-10 flex flex-wrap gap-4"
        >
          {COURSE.badges.map((b) => (
            <motion.div
              key={b.label}
              variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
              className="rounded-2xl border border-[#293891]/10 bg-[#FFFFFF]/70 px-6 py-4 backdrop-blur-md"
            >
              <div className="font-display text-2xl font-semibold text-[#293891]">{b.label}</div>
              <div className="mt-0.5 text-xs uppercase tracking-[0.15em] text-[#566076]">{b.sub}</div>
            </motion.div>
          ))}
        </motion.div>

        <motion.a
          href="#overview"
          data-testid="hero-scroll-cue"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="mt-14 inline-flex items-center gap-3 text-sm font-medium text-[#566076] transition-colors hover:text-[#293891]"
        >
          <motion.span
            animate={{ y: [0, 6, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#293891]/15"
          >
            <ArrowDown size={15} />
          </motion.span>
          Scroll to explore the program
        </motion.a>
      </motion.div>

      {/* Data-network visual — dark panel below text on mobile, right-side panel on desktop */}
      <motion.div
        style={{ y: visualY }}
        className="pointer-events-none relative z-0 mt-12 w-full lg:absolute lg:inset-y-0 lg:left-[50%] lg:right-0 lg:mt-0 lg:flex lg:w-auto lg:items-center lg:justify-center"
      >
        <div className="mx-auto w-full max-w-[430px] px-2 sm:max-w-[500px] lg:max-w-none lg:px-4">
          <NetworkVisual />
        </div>
      </motion.div>
    </section>
  );
}
